import axios from "axios";
import { CheckCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from '../styles/JobApplicationForm.module.css'; // Import the CSS module

const JobApplicationForm = ({ jobTitle, jobId, isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    mode: 'onBlur', // Validate fields when the user clicks away
  });

  // Reset form state when the modal is reopened for a new job
  useEffect(() => {
    if (isOpen) {
      reset();
      setShowSuccess(false);
    }
  }, [isOpen, reset]);

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result;
        // result is like "data:<mime-type>;base64,<base64data>"
        const base64String = result.split(',')[1]; // get only base64 part
        resolve(base64String);
      };
      reader.onerror = reject;
    });



  const onSubmit = async (data) => {
    console.log("Form data:", data);
    console.log("Job ID:", jobId);
    setIsSubmitting(true);

    try {
      const base64Resume = await convertToBase64(data.resume[0]);
      const resumeFilename = data.resume[0].name;


      const payload = {
        jsonrpc: "2.0",
        params: {
          "job_id": jobId,
          name: data.name,
          email: data.email,
          phone: data.phone,
          resume: base64Resume,
          resume_filename: resumeFilename,
        },
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/apply`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      console.log("Response data:", response.data.result.result.status);

      if (response.data.result.result.status === "success") {
        setShowSuccess(true);
      } else {
        const message = response.data?.message || response.data?.result?.message;
        alert(message || "Submission failed.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };




  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.content}>
          {!showSuccess ? (
            <>
              <div className={styles.header}>
                <h2 className={styles.title}>
                  Apply for: <span className={styles.jobTitle}>{jobTitle}</span>
                </h2>
                <button onClick={handleClose} className={styles.closeButton}>
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>Full Name *</label>
                  <input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    className={styles.input}
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Name must be at least 2 characters' }
                    })}
                  />
                  {errors.name && <p className={styles.errorMessage}>{errors.name.message}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>Email Address *</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    className={styles.input}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' }
                    })}
                  />
                  {errors.email && <p className={styles.errorMessage}>{errors.email.message}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone" className={styles.label}>Phone Number *</label>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="Enter your phone number"
                    className={styles.input}
                    {...register('phone', {
                      required: 'Phone number is required',
                      minLength: { value: 10, message: 'Phone number must be at least 10 digits' }
                    })}
                  />
                  {errors.phone && <p className={styles.errorMessage}>{errors.phone.message}</p>}
                </div>



                <div className={styles.formGroup}>
                  <label htmlFor="resume" className={styles.label}>Resume/CV *</label>
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className={styles.input}
                    {...register('resume', { required: 'Please upload your resume' })}
                  />
                  {errors.resume && <p className={styles.errorMessage}>{errors.resume.message}</p>}
                </div>

                <div className={styles.buttonGroup}>
                  <button type="button" onClick={handleClose} disabled={isSubmitting} className={`${styles.button} ${styles.cancelButton}`}>
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className={`${styles.button} ${styles.submitButton}`}>
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <SuccessSection handleClose={handleClose} jobTitle={jobTitle} />
          )}
        </div>
      </div>
    </div>
  );
};

const SuccessSection = ({ handleClose, jobTitle }) => (
  <div className={styles.successSection}>
    <CheckCircle size={64} className={styles.successIcon} />
    <h3 className={styles.successTitle}>Application Submitted!</h3>
    <p className={styles.successMessage}>
      Thank you for applying to <strong className={styles.jobTitle}>{jobTitle}</strong>. We've received it.
    </p>

    <div className={styles.nextStepsBox}>
      <h4 className={styles.nextStepsTitle}>
        <CheckCircle size={20} />
        What happens next?
      </h4>
      <div className={styles.nextStepsList}>
        <span className={styles.nextStepsItem}><CheckCircle size={16} /> Your application is under review.</span>
        <span className={styles.nextStepsItem}><CheckCircle size={16} /> We'll contact you in 3-5 business days.</span>
        <span className={styles.nextStepsItem}><CheckCircle size={16} /> Please check your email for updates.</span>
      </div>
    </div>
    <button onClick={handleClose} className={`${styles.button} ${styles.doneButton}`}>
      Done
    </button>
  </div>
);

export default JobApplicationForm;